import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ArrowUpRight, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useCart, PRODUCT } from "../context/CartContext";
import { useShopProduct } from "../lib/useShopProduct";
import { lockScroll } from "../lib/scroll";
import { EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export default function CartDrawer() {
  const { items, isOpen, closeCart, setQty, removeItem, clear, subtotal, count } = useCart();
  const live = useShopProduct();
  const [step, setStep] = useState("cart");
  const [placing, setPlacing] = useState(false);

  const liveVariantId =
    live?.variants?.find((v) => v.available !== false)?.id ||
    live?.variants?.[0]?.id ||
    null;
  const shopifyReady = items.length > 0 && !!liveVariantId;

  useEffect(() => {
    lockScroll(isOpen);
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) return;
    const t = setTimeout(() => setStep("cart"), 500);
    return () => clearTimeout(t);
  }, [isOpen]);

  const checkoutShopify = async () => {
    if (!shopifyReady) {
      toast.error("Shopify checkout is unavailable right now.");
      return;
    }
    setPlacing(true);
    try {
      const res = await fetch(`${API}/shopify/cart`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((i) => ({
            variantId: i.variantId || liveVariantId,
            qty: i.qty,
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.detail || "checkout failed");
      clear();
      setStep("done");
      setTimeout(() => {
        window.location.href = data.checkoutUrl;
      }, 700);
    } catch {
      toast.error("Shopify checkout unavailable — try Amazon instead.");
      setPlacing(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div data-testid="cart-drawer" className="fixed inset-0 z-[75]">
          <motion.div
            className="absolute inset-0 bg-ink/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            onClick={closeCart}
          />
          <motion.aside
            className="absolute right-0 top-0 bottom-0 w-full max-w-[440px] bg-ivory flex flex-col shadow-2xl"
            initial={{ x: "102%" }}
            animate={{ x: 0 }}
            exit={{ x: "102%" }}
            transition={{ duration: 0.65, ease: EASE }}
          >
            {/* header */}
            <div className="flex items-center justify-between px-7 py-6 border-b border-ink/8">
              <h3 className="font-display text-2xl">
                {step === "cart" && `Your Cart (${count})`}
                {step === "done" && "Order received"}
              </h3>
              <button
                data-testid="cart-close-button"
                onClick={closeCart}
                className="p-2 -mr-2 text-ink/60 hover:text-ink transition-colors"
                aria-label="Close cart"
              >
                <X size={19} strokeWidth={1.5} />
              </button>
            </div>

            {/* CART STEP */}
            {step === "cart" && (
              <>
                <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6" data-lenis-prevent>
                  {items.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-16" data-testid="cart-empty-state">
                      <span className="w-14 h-14 rounded-full bg-lavsoft flex items-center justify-center">
                        <img src="/images/logo-vi-black.png" alt="vi" className="h-5 w-auto" />
                      </span>
                      <p className="font-display text-xl">Your cart is empty</p>
                      <p className="font-light text-sm text-stone max-w-[240px]">
                        Discover the Luxe Hydrating Cream and begin your ritual.
                      </p>
                      <button
                        data-testid="cart-empty-shop-button"
                        onClick={closeCart}
                        className="btn-lux mt-2 bg-ink text-ivory text-[11px] uppercase tracking-widest2 px-7 py-3 rounded-full"
                      >
                        Shop Now
                      </button>
                    </div>
                  )}
                  {items.map((i) => (
                    <motion.div
                      key={i.id}
                      data-testid="cart-item"
                      layout
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="flex gap-4"
                    >
                      <div className="w-[76px] h-[92px] rounded-2xl overflow-hidden bg-porcelain shrink-0">
                        <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 flex flex-col justify-between py-0.5">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-display text-lg leading-tight">{i.name}</p>
                            <p className="font-sans text-[10px] uppercase tracking-wide2 text-stone mt-1">
                              {i.size}
                            </p>
                          </div>
                          <button
                            data-testid={`cart-remove-${i.id}`}
                            onClick={() => removeItem(i.id)}
                            className="text-ink/35 hover:text-ink transition-colors p-1"
                            aria-label={`Remove ${i.name}`}
                          >
                            <Trash2 size={14} strokeWidth={1.5} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center border border-ink/12 rounded-full">
                            <button
                              data-testid={`cart-qty-minus-${i.id}`}
                              onClick={() => setQty(i.id, i.qty - 1)}
                              className="px-2.5 py-1.5 text-ink/60 hover:text-ink"
                              aria-label="Decrease quantity"
                            >
                              <Minus size={12} strokeWidth={2} />
                            </button>
                            <span data-testid={`cart-qty-${i.id}`} className="w-6 text-center text-sm">{i.qty}</span>
                            <button
                              data-testid={`cart-qty-plus-${i.id}`}
                              onClick={() => setQty(i.id, i.qty + 1)}
                              className="px-2.5 py-1.5 text-ink/60 hover:text-ink"
                              aria-label="Increase quantity"
                            >
                              <Plus size={12} strokeWidth={2} />
                            </button>
                          </div>
                          <p className="font-display text-lg">${(i.price * i.qty).toFixed(2)}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                {items.length > 0 && (
                  <div className="border-t border-ink/8 px-7 py-6 space-y-4">
                    <div className="flex justify-between font-light text-sm text-stone">
                      <span>Subtotal</span>
                      <span data-testid="cart-subtotal" className="text-ink">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-light text-sm text-stone">
                      <span>Shipping</span>
                      <span>Calculated at checkout</span>
                    </div>
                    <button
                      data-testid="cart-checkout-button"
                      onClick={checkoutShopify}
                      disabled={placing || !shopifyReady}
                      className="btn-lux w-full bg-ink text-ivory text-xs uppercase tracking-widest2 py-4 rounded-full flex items-center justify-center gap-2.5 disabled:opacity-50"
                    >
                      {placing ? "Opening Shopify…" : (
                        <>
                          Checkout <Lock size={12} strokeWidth={1.5} />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* DONE STEP */}
            {step === "done" && (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-10 gap-5" data-testid="order-success">
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="w-16 h-16 rounded-full bg-lavsoft flex items-center justify-center"
                >
                  <CheckCircle2 size={26} strokeWidth={1.5} className="text-lavdeep" />
                </motion.span>
                <h4 className="font-display text-3xl">Your ritual awaits</h4>
                <p className="font-light text-sm text-stone leading-relaxed max-w-[280px]">
                  Taking you to secure checkout…
                </p>
                <button
                  data-testid="success-amazon-button"
                  onClick={() => window.open(PRODUCT.amazonUrl, "_blank", "noopener,noreferrer")}
                  className="btn-lux mt-2 bg-ink text-ivory text-xs uppercase tracking-widest2 px-8 py-4 rounded-full flex items-center gap-2.5"
                >
                  Continue on Amazon <ArrowUpRight size={14} strokeWidth={1.5} />
                </button>
                <button
                  data-testid="success-close-button"
                  onClick={closeCart}
                  className="hover-line font-sans text-[11px] uppercase tracking-widest2 text-stone"
                >
                  Close
                </button>
              </div>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
